import { useCallback, useState } from "react";
import { useDropzone, type FileRejection } from "react-dropzone";
import { Upload, FileSpreadsheet, X, FileText } from "lucide-react";
import { Button } from "@/ui/atoms";
import { cn } from "@/utils/utils";

export interface TFileUploadZoneProps {
    onFileSelect?: (file: File) => void;
    onFilesSelect?: (files: File[]) => void;
    accept?: Record<string, string[]>;
    maxSize?: number;
    multiple?: boolean;
    maxFiles?: number;
    disabled?: boolean;
    className?: string;
    /** Id for the hidden file input (for label association / accessibility) */
    id?: string;
    /**
     * Suppresses the internal "N of maxFiles files selected" list and the
     * maxFiles cap-error text in `multiple` mode, for a caller that renders its
     * own per-file list and owns both cap enforcement and removal itself. When
     * set, this component keeps no accumulated selection of its own — each
     * `onFilesSelect` call reports only the files accepted in that specific
     * drop/pick, and `maxFiles` is not used to truncate anything. The caller
     * is the single source of truth for the full selection, so a file the
     * caller has removed can never be resurrected by a later drop. Defaults to
     * false so every existing consumer is unaffected.
     */
    hideFileList?: boolean;
}

const defaultAccept = {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "application/vnd.ms-excel": [".xls"],
    "text/csv": [".csv"],
};

function getExtensionsFromAccept(accept: Record<string, string[]>): string {
    const exts = Object.values(accept).flat();
    return exts.join(", ");
}

export function FileUploadZone({
    onFileSelect,
    onFilesSelect,
    accept = defaultAccept,
    maxSize = 50 * 1024 * 1024, // 50MB
    multiple = false,
    maxFiles = 1,
    disabled = false,
    className,
    id,
    hideFileList = false,
}: TFileUploadZoneProps) {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [error, setError] = useState<string | null>(null);

    // hideFileList delegates cap enforcement to the caller, so there is no
    // internal limit to disable the dropzone against.
    const isAtLimit = multiple && !hideFileList && selectedFiles.length >= maxFiles;

    const onDrop = useCallback(
        (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
            setError(null);

            if (rejectedFiles.length > 0) {
                const firstRejection = rejectedFiles[0];
                const firstError = firstRejection?.errors[0];
                if (firstError?.code === "too-many-files") {
                    setError(`Maximum ${maxFiles} files allowed`);
                } else if (firstError) {
                    setError(firstError.message);
                }
                return;
            }

            if (multiple) {
                if (hideFileList) {
                    // The caller owns the full selection, including removal —
                    // hand back exactly what was accepted in this drop/pick.
                    // There is no internal accumulation here to fall out of
                    // sync with the caller's own state (see FIN-611 review:
                    // accumulating internally meant a file the caller had
                    // already removed could be resurrected by a later drop).
                    onFilesSelect?.(acceptedFiles);
                    return;
                }
                setSelectedFiles((prev) => {
                    const remaining = maxFiles - prev.length;
                    if (remaining <= 0) return prev;
                    const toAdd = acceptedFiles.slice(0, remaining);
                    const next = [...prev, ...toAdd];
                    onFilesSelect?.(next);
                    if (acceptedFiles.length > remaining) {
                        setError(`Maximum ${maxFiles} files allowed`);
                    }
                    return next;
                });
            } else if (acceptedFiles.length > 0) {
                const file = acceptedFiles[0];
                if (file) {
                    setSelectedFile(file);
                    onFileSelect?.(file);
                }
            }
        },
        [multiple, maxFiles, hideFileList, onFileSelect, onFilesSelect],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept,
        maxSize,
        maxFiles: multiple ? undefined : 1,
        multiple,
        disabled: disabled || isAtLimit,
        useFsAccessApi: false,
    });

    const clearFile = () => {
        setSelectedFile(null);
        setError(null);
    };

    const removeFile = (index: number) => {
        setSelectedFiles((prev) => {
            const next = prev.filter((_, i) => i !== index);
            onFilesSelect?.(next);
            return next;
        });
    };

    const formatsList = getExtensionsFromAccept(accept);

    if (multiple) {
        return (
            <div className={className}>
                <div
                    {...getRootProps()}
                    className={cn(
                        "relative rounded-lg p-8 text-center transition-colors",
                        isAtLimit
                            ? "cursor-not-allowed bg-muted/30 opacity-60"
                            : isDragActive
                              ? "bg-primary/10"
                              : "bg-muted/50 hover:bg-muted",
                        disabled && "cursor-not-allowed opacity-50",
                        error && "bg-destructive/10 text-destructive",
                    )}
                >
                    <input {...getInputProps({ id })} />
                    <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
                    {isAtLimit ? (
                        <>
                            <p className="mt-4 text-lg font-medium">Maximum files reached</p>
                            <p className="mt-2 text-sm text-muted-foreground">
                                Remove a file below to add a different one
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="mt-4 text-lg font-medium">
                                {isDragActive ? "Drop files here" : "Drag & drop your files here"}
                            </p>
                            <p className="mt-2 text-sm text-muted-foreground">or click to browse</p>
                            <p className="mt-4 text-xs text-muted-foreground">
                                Supported formats: {formatsList} (max {maxSize / 1024 / 1024}MB per
                                file, up to {maxFiles} files)
                            </p>
                        </>
                    )}
                </div>

                {!hideFileList && selectedFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                        <p className="text-sm font-medium text-muted-foreground">
                            {selectedFiles.length} of {maxFiles} files selected
                        </p>
                        {selectedFiles.map((file, index) => (
                            <div
                                key={`${file.name}-${index}`}
                                className="flex items-center gap-3 rounded-lg border px-3 py-2"
                            >
                                <FileText className="h-5 w-5 shrink-0 text-primary" />
                                <div className="min-w-0 flex-1 text-left">
                                    <p className="truncate text-sm font-medium">{file.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                        {(file.size / 1024 / 1024).toFixed(2)} MB
                                    </p>
                                </div>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeFile(index);
                                    }}
                                    disabled={disabled}
                                    className="shrink-0"
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}

                {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
            </div>
        );
    }

    return (
        <div className={className}>
            <div
                {...getRootProps()}
                className={cn(
                    "relative rounded-lg p-8 text-center transition-colors",
                    isDragActive ? "bg-primary/10" : "bg-muted/50 hover:bg-muted",
                    disabled && "cursor-not-allowed opacity-50",
                    error && "bg-destructive/10 text-destructive",
                )}
            >
                <input {...getInputProps({ id })} />

                {selectedFile ? (
                    <div className="flex items-center justify-center gap-3">
                        <FileSpreadsheet className="h-10 w-10 text-primary" />
                        <div className="text-left">
                            <p className="font-medium">{selectedFile.name}</p>
                            <p className="text-sm text-muted-foreground">
                                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                                e.stopPropagation();
                                clearFile();
                            }}
                            className="ml-4"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                ) : (
                    <>
                        <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
                        <p className="mt-4 text-lg font-medium">
                            {isDragActive ? "Drop file here" : "Drag & drop your file here"}
                        </p>
                        <p className="mt-2 text-sm text-muted-foreground">or click to browse</p>
                        <p className="mt-4 text-xs text-muted-foreground">
                            Supported formats: {formatsList} (max {maxSize / 1024 / 1024}MB)
                        </p>
                    </>
                )}
            </div>

            {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
        </div>
    );
}
